import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Button, Container, Table, Spinner, Alert, Row, Col, Card, Badge, Form, Modal } from 'react-bootstrap';
import { getCashRegisterSummary, getCashRegisterHistory, getAllOpenRegisters, getCashRegisterStations, forceCloseCashRegister } from '../services/api';
import { trackAction } from '../services/analytics';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Pagination from '../components/Pagination';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import AuthContext from '../context/AuthContext';
import PageSizeSelector from '../components/PageSizeSelector';
import { FaCashRegister, FaArrowUp, FaArrowDown, FaHistory, FaTable, FaTh } from 'react-icons/fa';
import { Monitor } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import useCashRegisterStatus from '../hooks/useCashRegisterStatus';
import OpenCashRegisterModal from '../components/OpenCashRegisterModal';
import CloseCashRegisterModal from '../components/CloseCashRegisterModal';
import CashRegisterStationsManager from '../components/CashRegisterStationsManager';

const PaymentMethodCard = ({ method, label, icon, data, formatCurrency }) => {
    const hasMovements = data.in > 0 || data.out > 0;
    return (
        <Card className={`h-100 border-0 shadow-sm ${!hasMovements ? 'opacity-50' : ''}`}>
            <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-semibold">{icon} {label}</span>
                </div>
                <div className="d-flex justify-content-between small">
                    <span className="text-success"><FaArrowUp className="me-1" />{formatCurrency(data.in)}</span>
                    <span className="text-danger"><FaArrowDown className="me-1" />{formatCurrency(data.out)}</span>
                </div>
                <div className="text-center mt-2 fw-bold" style={{ fontSize: '1.1rem' }}>
                    {formatCurrency(data.balance)}
                </div>
            </Card.Body>
        </Card>
    );
};

const CashRegisterPage = () => {
    const { user } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    const { isOpen, cashRegister, canOpenAStation, loading: statusLoading, refetch: refetchStatus } = useCashRegisterStatus();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [summary, setSummary] = useState(null);

    // History
    const [history, setHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(0);
    const [historyPageSize, setHistoryPageSize] = useState(10);
    const [historyStationFilter, setHistoryStationFilter] = useState('');

    // Stations (for filter dropdown)
    const [stations, setStations] = useState([]);

    // All open registers panel (manager view)
    const [allOpenRegisters, setAllOpenRegisters] = useState([]);
    const [loadingOpenRegisters, setLoadingOpenRegisters] = useState(false);

    // Modals
    const [showOpenModal, setShowOpenModal] = useState(false);
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showStationsModal, setShowStationsModal] = useState(false);

    const [viewMode, setViewMode] = useState(() => localStorage.getItem('cashRegisterViewMode') || 'table');

    const toggleViewMode = (mode) => {
        setViewMode(mode);
        localStorage.setItem('cashRegisterViewMode', mode);
    };

    const canManageStations = hasPermission(PERMISSIONS.MANAGE_CASH_REGISTER_STATIONS);

    const formatCurrency = (amount) =>
        `$${parseFloat(amount || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

    const fetchSummary = useCallback(async () => {
        try {
            setLoading(true);
            if (isOpen) {
                const summaryResponse = await getCashRegisterSummary();
                setSummary(summaryResponse.data);
            } else {
                setSummary(null);
            }
            setError('');
        } catch (error) {
            console.error('Error fetching cash register summary:', error);
            if (error.response?.status === 403) {
                setError(error.response.data.message || t('cashRegisterPage.toasts.permissionError'));
            } else {
                toast.error(t('cashRegisterPage.toasts.fetchFailed'));
            }
        } finally {
            setLoading(false);
        }
    }, [isOpen, t]);

    const fetchHistory = useCallback(async () => {
        try {
            const params = { page: historyPage, limit: historyPageSize };
            if (historyStationFilter) params.station_id = historyStationFilter;
            const response = await getCashRegisterHistory(params);
            setHistory(response.data.history);
            setHistoryTotalPages(response.data.totalPages);
        } catch (error) {
            console.error('Error fetching history:', error);
        }
    }, [historyPage, historyPageSize, historyStationFilter]);

    const fetchAllOpenRegisters = useCallback(async () => {
        if (!canManageStations) return;
        try {
            setLoadingOpenRegisters(true);
            const res = await getAllOpenRegisters();
            setAllOpenRegisters(res.data);
        } catch {
            // Silently ignore — may not have permission
        } finally {
            setLoadingOpenRegisters(false);
        }
    }, [canManageStations]);

    const fetchStations = useCallback(async () => {
        if (!canManageStations) return;
        try {
            const res = await getCashRegisterStations();
            setStations(res.data);
        } catch {
            // ignore
        }
    }, [canManageStations]);

    const handleForceClose = useCallback(async (reg) => {
        const result = await Swal.fire({
            title: t('cashRegisterPage.forceClose.confirmTitle', '¿Cerrar caja?'),
            html: `<p>${t('cashRegisterPage.forceClose.confirmText', 'Se cerrará la caja de')} <strong>${reg.user_name}</strong> (${reg.station_name}).<br/>${t('cashRegisterPage.forceClose.confirmSubtext', 'El efectivo esperado se usará como monto real.')}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('cashRegisterPage.forceClose.confirm', 'Sí, cerrar'),
            cancelButtonText: t('cashRegisterPage.forceClose.cancel', 'Cancelar'),
            confirmButtonColor: 'var(--ev-action-primary-bg)'
        });

        if (!result.isConfirmed) return;

        try {
            await forceCloseCashRegister(reg.id);
            trackAction('cash_register_force_closed');
            toast.success(t('cashRegisterPage.forceClose.success', 'Caja cerrada exitosamente'));
            fetchAllOpenRegisters();
            refetchStatus();
        } catch (err) {
            toast.error(t('cashRegisterPage.forceClose.error', 'Error al cerrar la caja'));
        }
    }, [t, fetchAllOpenRegisters, refetchStatus]);

    useEffect(() => {
        if (hasPermission(PERMISSIONS.VIEW_CASH_REGISTER)) {
            fetchSummary();
            fetchHistory();
            fetchAllOpenRegisters();
            fetchStations();
        } else {
            setLoading(false);
            setError(t('cashRegisterPage.toasts.permissionError'));
        }
    }, [fetchSummary, fetchHistory, fetchAllOpenRegisters, fetchStations, hasPermission, t]);

    const handleCashRegisterOpened = () => {
        // El evento cash_register_opened se registra en OpenCashRegisterModal (fuente única,
        // usada también desde Ventas/Gastos), para no depender de esta página ni duplicar.
        refetchStatus();
        fetchSummary();
        fetchHistory();
        fetchAllOpenRegisters();
        setShowOpenModal(false);
    };

    const handleStationFilterChange = (e) => {
        setHistoryStationFilter(e.target.value);
        setHistoryPage(1);
    };

    const paymentMethodLabels = {
        cash: { label: t('cashRegisterPage.methods.cash'), icon: '💵' },
        nequi: { label: 'Nequi', icon: '📱' },
        daviplata: { label: 'Daviplata', icon: '📲' },
        card: { label: t('cashRegisterPage.methods.card'), icon: '💳' },
        bankTransfer: { label: t('cashRegisterPage.methods.bankTransfer'), icon: '🏦' },
        other: { label: t('cashRegisterPage.methods.other'), icon: '📊' }
    };

    return (
        <Container className="mt-4 pb-5">
            <Row className="align-items-center mb-4">
                <Col md={6}>
                    <h2><FaCashRegister className="me-2" />{t('cashRegisterPage.title')}</h2>
                    <p className="text-muted mb-0">{t('cashRegisterPage.subtitle')}</p>
                </Col>
                <Col md={6} className="text-end d-flex gap-2 justify-content-end align-items-center">
                    {canManageStations && (
                        <Button variant="outline-secondary" onClick={() => setShowStationsModal(true)}>
                            <Monitor size={15} className="me-2" />
                            {t('cashRegisterPage.stations.manage', 'Gestionar Cajas')}
                        </Button>
                    )}
                    {!isOpen && hasPermission(PERMISSIONS.OPEN_CASH_REGISTER) && canOpenAStation && (
                        <Button variant="success" onClick={() => setShowOpenModal(true)}>
                            <FaCashRegister className="me-2" />
                            {t('cashRegisterPage.salesPageIntegration.openCashRegister')}
                        </Button>
                    )}
                    {isOpen && hasPermission(PERMISSIONS.CLOSE_CASH_REGISTER) && (
                        <Button variant="dark" onClick={() => setShowCloseModal(true)}>
                            <FaCashRegister className="me-2" />
                            {t('cashRegisterPage.salesPageIntegration.closeCashRegister')}
                        </Button>
                    )}
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
                <>
                    {/* All Open Registers — manager view (OWNER / MANAGE_CASH_REGISTER_STATIONS) */}
                    {canManageStations && (
                        <Card className="mb-4 border-0 shadow-sm">
                            <Card.Body>
                                <h6 className="fw-semibold text-muted text-uppercase mb-3 d-flex align-items-center gap-2">
                                    <Monitor size={15} />
                                    {t('cashRegisterPage.stations.openRegisters', 'Cajas Abiertas')}
                                </h6>
                                {loadingOpenRegisters ? (
                                    <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
                                ) : allOpenRegisters.length === 0 ? (
                                    <p className="text-muted small mb-0">{t('cashRegisterPage.stations.noOpenRegisters', 'No hay cajas abiertas en este momento.')}</p>
                                ) : (
                                    <Row className="g-2">
                                        {allOpenRegisters.map(reg => {
                                            const isOwnRegister = cashRegister?.id === reg.id;
                                            return (
                                                <Col key={reg.id} xs={12} md={6} lg={4}>
                                                    <Card className="border border-success-subtle bg-success-subtle p-2">
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <div>
                                                                <div className="fw-semibold">{reg.station_name}</div>
                                                                <div className="text-muted small">{reg.user_name}</div>
                                                                <div className="small mt-1">
                                                                    <span className="me-2">⏱ {new Date(reg.opened_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</span>
                                                                    <span className="text-success fw-medium">{formatCurrency(reg.total_collected)}</span>
                                                                </div>
                                                            </div>
                                                            {!isOwnRegister && (
                                                                <Button
                                                                    variant="dark"
                                                                    size="sm"
                                                                    className="ms-2 flex-shrink-0"
                                                                    onClick={() => handleForceClose(reg)}
                                                                    title={t('cashRegisterPage.forceClose.button', 'Cerrar caja')}
                                                                >
                                                                    <FaCashRegister />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Current Status Card */}
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={3}>
                                    <h6 className="text-muted text-uppercase mb-1">{t('cashRegisterPage.currentStatus')}</h6>
                                    <Badge bg={isOpen ? 'success' : 'secondary'} className="fs-6 px-3 py-2">
                                        {isOpen ? t('cashRegisterPage.statusOpen') : t('cashRegisterPage.statusClosed')}
                                    </Badge>
                                    {isOpen && cashRegister?.station_name && (
                                        <div className="text-muted small mt-1">
                                            <Monitor size={12} className="me-1" />
                                            {cashRegister.station_name}
                                        </div>
                                    )}
                                </Col>
                                {cashRegister && isOpen && (
                                    <>
                                        <Col md={3}>
                                            <h6 className="text-muted text-uppercase mb-1">{t('cashRegisterPage.openingAmount')}</h6>
                                            <h4 className="mb-0">{formatCurrency(cashRegister.opening_amount)}</h4>
                                        </Col>
                                        <Col md={3}>
                                            <h6 className="text-muted text-uppercase mb-1">{t('cashRegisterPage.expectedCash')}</h6>
                                            <h4 className="mb-0 text-primary">{summary ? formatCurrency(summary.totals.expectedCash) : '-'}</h4>
                                        </Col>
                                        <Col md={3}>
                                            <h6 className="text-muted text-uppercase mb-1">{t('cashRegisterPage.openedAt')}</h6>
                                            <span>{new Date(cashRegister.opened_at).toLocaleString('es-CO')}</span>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Payment Methods Summary */}
                    {isOpen && summary && (
                        <>
                            <h5 className="mb-3">{t('cashRegisterPage.movements')}</h5>
                            <Row className="mb-4 g-3">
                                {Object.entries(paymentMethodLabels).map(([method, { label, icon }]) => (
                                    <Col xs={6} md={4} lg={2} key={method}>
                                        <PaymentMethodCard
                                            method={method}
                                            label={label}
                                            icon={icon}
                                            data={summary.summary[method] || { in: 0, out: 0, balance: 0 }}
                                            formatCurrency={formatCurrency}
                                        />
                                    </Col>
                                ))}
                            </Row>

                            {/* Totals */}
                            <Card className="mb-4 border-0 shadow-sm bg-light">
                                <Card.Body>
                                    <Row className="text-center">
                                        <Col md={4}>
                                            <h6 className="text-muted">{t('cashRegisterPage.totalIn')}</h6>
                                            <h3 className="text-success">{formatCurrency(summary.totals.totalIn)}</h3>
                                        </Col>
                                        <Col md={4}>
                                            <h6 className="text-muted">{t('cashRegisterPage.totalOut')}</h6>
                                            <h3 className="text-danger">{formatCurrency(summary.totals.totalOut)}</h3>
                                        </Col>
                                        <Col md={4}>
                                            <h6 className="text-muted">{t('cashRegisterPage.netBalance')}</h6>
                                            <h3 className={summary.totals.netBalance >= 0 ? 'text-success' : 'text-danger'}>
                                                {formatCurrency(summary.totals.netBalance)}
                                            </h3>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </>
                    )}

                    {/* History */}
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h5 className="mb-0"><FaHistory className="me-2" />{t('cashRegisterPage.history')}</h5>
                        <div className="d-flex align-items-center gap-2">
                            {stations.length > 1 && (
                                <Form.Select
                                    size="sm"
                                    style={{ width: 'auto', minWidth: 180 }}
                                    value={historyStationFilter}
                                    onChange={handleStationFilterChange}
                                >
                                    <option value="">{t('cashRegisterPage.stations.allStations', 'Todas las estaciones')}</option>
                                    {stations.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                            )}
                            <div
                                className="sales-view-toggle"
                                role="group"
                                aria-label={t('cashRegisterPage.viewToggle.label', 'Cambiar vista')}
                            >
                                <button
                                    id="cash-register-view-table"
                                    className={`sales-view-toggle__btn${viewMode === 'table' ? ' sales-view-toggle__btn--active' : ''}`}
                                    onClick={() => toggleViewMode('table')}
                                    aria-pressed={viewMode === 'table'}
                                    title={t('cashRegisterPage.viewToggle.tableView', 'Vista tabla')}
                                    aria-label={t('cashRegisterPage.viewToggle.tableView', 'Vista tabla')}
                                >
                                    <FaTable aria-hidden="true" />
                                    <span className="sales-view-toggle__label">{t('cashRegisterPage.viewToggle.tableView', 'Tabla')}</span>
                                </button>
                                <button
                                    id="cash-register-view-cards"
                                    className={`sales-view-toggle__btn${viewMode === 'cards' ? ' sales-view-toggle__btn--active' : ''}`}
                                    onClick={() => toggleViewMode('cards')}
                                    aria-pressed={viewMode === 'cards'}
                                    title={t('cashRegisterPage.viewToggle.cardsView', 'Vista tarjetas')}
                                    aria-label={t('cashRegisterPage.viewToggle.cardsView', 'Vista tarjetas')}
                                >
                                    <FaTh aria-hidden="true" />
                                    <span className="sales-view-toggle__label">{t('cashRegisterPage.viewToggle.cardsView', 'Tarjetas')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    {viewMode === 'table' ? (
                    <div className="modern-table-wrapper">
                        <Table hover responsive className="modern-table">
                            <thead>
                                <tr>
                                    <th>{t('cashRegisterPage.table.openedAt')}</th>
                                    <th>{t('cashRegisterPage.table.closedAt')}</th>
                                    <th className="text-end">{t('cashRegisterPage.openingAmount')}</th>
                                    <th className="text-end">{t('cashRegisterPage.expectedCash')}</th>
                                    <th className="text-end">{t('cashRegisterPage.actualCash')}</th>
                                    <th className="text-end">{t('cashRegisterPage.difference')}</th>
                                    <th>{t('cashRegisterPage.table.user')}</th>
                                    <th>{t('cashRegisterPage.stations.currentStation', 'Estación')}</th>
                                    <th>{t('cashRegisterPage.table.status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.length > 0 ? history.map(record => (
                                    <tr key={record.id}>
                                        <td>{new Date(record.opened_at).toLocaleString('es-CO')}</td>
                                        <td>{record.closed_at ? new Date(record.closed_at).toLocaleString('es-CO') : '-'}</td>
                                        <td className="text-end">{formatCurrency(record.opening_amount)}</td>
                                        <td className="text-end">{record.expected_cash ? formatCurrency(record.expected_cash) : '-'}</td>
                                        <td className="text-end">{record.actual_cash ? formatCurrency(record.actual_cash) : '-'}</td>
                                        <td className={`text-end ${parseFloat(record.cash_difference) < 0 ? 'text-danger' : parseFloat(record.cash_difference) > 0 ? 'text-success' : ''}`}>
                                            {record.cash_difference !== null ? formatCurrency(record.cash_difference) : '-'}
                                        </td>
                                        <td>{record.user_name || '-'}</td>
                                        <td>{record.station_name || '-'}</td>
                                        <td>
                                            <Badge bg={record.status === 'open' ? 'success' : 'secondary'}>
                                                {record.status === 'open' ? t('cashRegisterPage.statusOpen') : t('cashRegisterPage.statusClosed')}
                                            </Badge>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="9" className="text-center py-4 text-muted">{t('cashRegisterPage.noHistory')}</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                    ) : (
                        /* ── Cards view ── */
                        <div>
                            {history.length === 0 ? (
                                <div className="text-center py-5 text-muted">{t('cashRegisterPage.noHistory')}</div>
                            ) : (
                                <div className="sales-cards-grid">
                                    {history.map(record => (
                                        <div key={record.id} className="sale-card">
                                            <div className="sale-card__header">
                                                <div className="sale-card__invoice">
                                                    <span className="sale-card__invoice-label">{record.station_name || '—'}</span>
                                                </div>
                                                <span className={`badge bg-${record.status === 'open' ? 'success' : 'secondary'} sale-card__badge`}>
                                                    {record.status === 'open' ? t('cashRegisterPage.statusOpen') : t('cashRegisterPage.statusClosed')}
                                                </span>
                                            </div>

                                            <div className="sale-card__client">
                                                <span className="sale-card__client-name">{record.user_name || '—'}</span>
                                            </div>

                                            <div className="sale-card__amounts">
                                                <div className="sale-card__amount-item">
                                                    <span className="sale-card__amount-label">{t('cashRegisterPage.openingAmount')}</span>
                                                    <span className="sale-card__amount-value sale-card__amount-value--total">{formatCurrency(record.opening_amount)}</span>
                                                </div>
                                                <div className="sale-card__amount-item">
                                                    <span className="sale-card__amount-label">{t('cashRegisterPage.expectedCash')}</span>
                                                    <span className="sale-card__amount-value">{record.expected_cash ? formatCurrency(record.expected_cash) : '-'}</span>
                                                </div>
                                                <div className="sale-card__amount-item">
                                                    <span className="sale-card__amount-label">{t('cashRegisterPage.actualCash')}</span>
                                                    <span className="sale-card__amount-value">{record.actual_cash ? formatCurrency(record.actual_cash) : '-'}</span>
                                                </div>
                                                <div className="sale-card__amount-item">
                                                    <span className="sale-card__amount-label">{t('cashRegisterPage.difference')}</span>
                                                    <span className={`sale-card__amount-value ${parseFloat(record.cash_difference) < 0 ? 'sale-card__amount-value--due' : parseFloat(record.cash_difference) > 0 ? 'sale-card__amount-value--paid' : ''}`}>
                                                        {record.cash_difference !== null ? formatCurrency(record.cash_difference) : '-'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="sale-card__dates">
                                                <div className="sale-card__date-item">
                                                    <span className="sale-card__date-label">{t('cashRegisterPage.table.openedAt')}</span>
                                                    <span className="sale-card__date-value">{new Date(record.opened_at).toLocaleString('es-CO')}</span>
                                                </div>
                                                <div className="sale-card__date-item">
                                                    <span className="sale-card__date-label">{t('cashRegisterPage.table.closedAt')}</span>
                                                    <span className="sale-card__date-value">{record.closed_at ? new Date(record.closed_at).toLocaleString('es-CO') : '-'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Pagination currentPage={historyPage} totalPages={historyTotalPages} onPageChange={setHistoryPage} />
                        <PageSizeSelector pageSize={historyPageSize} onPageSizeChange={(e) => { setHistoryPageSize(parseInt(e.target.value, 10)); setHistoryPage(1); }} />
                    </div>
                </>
            )}

            {/* Stations Management Modal */}
            <Modal
                show={showStationsModal}
                onHide={() => { setShowStationsModal(false); refetchStatus(); fetchStations(); }}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Monitor size={18} className="me-2" />
                        {t('cashRegisterPage.stations.manage', 'Gestionar Cajas')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CashRegisterStationsManager />
                </Modal.Body>
            </Modal>

            {/* Open Cash Register Modal */}
            <OpenCashRegisterModal
                show={showOpenModal}
                onHide={() => setShowOpenModal(false)}
                onSuccess={handleCashRegisterOpened}
            />

            {/* Close Cash Register Modal */}
            <CloseCashRegisterModal
                show={showCloseModal}
                onHide={() => setShowCloseModal(false)}
                cashRegister={cashRegister}
                summary={summary}
                onSuccess={() => {
                    setSummary(null);
                    refetchStatus();
                    fetchHistory();
                    fetchAllOpenRegisters();
                    setShowCloseModal(false);
                }}
            />
        </Container>
    );
};

export default CashRegisterPage;
